import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import styled from 'styled-components';
import { USE_API } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  clearCollectionDetail,
  fetchCollectionDetail,
} from '../store/actions/collectionActions';
import { Container } from '../components/layout/Primitives';
import { ProductCard } from '../components/commerce/ProductCard';
import { ProductCardsGrid } from '../components/commerce/ProductCardLayout';
import { siteImagery } from '../config/siteImagery';
import { mapApiProductsToModamaiCatalog } from '../utils/mapApiProduct';
import CollectionRichHtml from '../components/content/CollectionRichHtml';
import ApiLocaleSeo from '../components/seo/ApiLocaleSeo';
import PageSeo from '../components/seo/PageSeo';

const BreadcrumbNav = styled.nav`
  padding: clamp(14px, 2vw, 18px) 0;
  font-size: clamp(10px, 0.12vw + 9px, 13px);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const CrumbLink = styled(Link)`
  color: inherit;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const HeroBand = styled.section`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: hsla(38, 25%, 92%, 0.45);
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 300;
  line-height: 1.06;
`;

const Sub = styled.p`
  margin: 0.75rem 0 0;
  max-width: 40rem;
  opacity: 0.72;
  font-size: ${({ theme }) => theme.type.body};
`;

const PageContentSection = styled.section`
  margin-top: clamp(2.5rem, 5vw, 4rem);
  padding-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  max-width: 48rem;
`;

const ContentEyebrow = styled.p`
  margin: 0 0 1rem;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #524d46;
`;

function heroImgFromMeta(image) {
  if (typeof image !== 'string' || !image.trim()) return siteImagery.heroMain;
  const u = image.trim();
  return u.startsWith('http') || u.startsWith('data:') ? u : siteImagery.heroMain;
}

function detailErrorMessage(detailError) {
  if (detailError == null) return '';
  if (typeof detailError === 'object' && detailError.message != null) {
    return String(detailError.message);
  }
  return String(detailError);
}

export default function CollectionDetail() {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  const { slug = '' } = useParams();
  const dispatch = useAppDispatch();
  const apiCollection = useAppSelector((s) => s.collection.detail);
  const detailLoading = useAppSelector((s) => s.collection.detailLoading);
  const detailError = useAppSelector((s) => s.collection.detailError);

  const items = useMemo(
    () =>
      apiCollection ? mapApiProductsToModamaiCatalog(apiCollection.products || []) : [],
    [apiCollection],
  );

  const normalizedSlug = (slug && String(slug).trim()) || '';
  const collectionMatches = Boolean(
    apiCollection &&
      normalizedSlug &&
      String(apiCollection.slug || '').toLowerCase() === normalizedSlug.toLowerCase(),
  );

  const errMsg = detailErrorMessage(detailError);
  const errStatus = typeof detailError === 'object' ? detailError?.status : undefined;
  const isNotFound =
    detailError &&
    (errStatus === 404 ||
      errMsg === 'Collection not found' ||
      /not found/i.test(errMsg));

  useLayoutEffect(() => {
    if (!USE_API || !normalizedSlug) return undefined;
    dispatch(fetchCollectionDetail(normalizedSlug, locale));
    return () => {
      dispatch(clearCollectionDetail());
    };
  }, [dispatch, normalizedSlug, locale]);

  const shouldShowLoading =
    USE_API &&
    Boolean(normalizedSlug) &&
    (detailLoading || (!detailError && !collectionMatches));

  if (!USE_API) {
    return (
      <Container style={{ padding: '3rem 1.25rem', textAlign: 'center' }}>
        <Title>{t('modamai.collectionDetail.apiOffTitle')}</Title>
        <Sub>{t('modamai.collectionDetail.apiOffSub')}</Sub>
      </Container>
    );
  }

  if (shouldShowLoading) {
    return (
      <Container style={{ padding: '3rem 1.25rem', textAlign: 'center' }}>
        <Title>{t('modamai.collectionDetail.loadingTitle')}</Title>
        <Sub>{t('modamai.collectionDetail.loadingSub')}</Sub>
      </Container>
    );
  }

  if (detailError) {
    return (
      <Container style={{ padding: '3rem 1.25rem', textAlign: 'center' }}>
        <Title>
          {isNotFound
            ? t('modamai.collectionDetail.notFoundTitle')
            : t('modamai.collectionDetail.unavailableTitle')}
        </Title>
        <Sub>{errMsg}</Sub>
        <div style={{ marginTop: '1.5rem' }}>
          <Link
            to="/collections"
            style={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 13 }}
          >
            {t('modamai.collectionDetail.backToCollections')}
          </Link>
        </div>
      </Container>
    );
  }

  if (!collectionMatches || !apiCollection) {
    return (
      <Container style={{ padding: '3rem 1.25rem', textAlign: 'center' }}>
        <Title>{t('modamai.collectionDetail.emptyStateTitle')}</Title>
        <Sub>{t('modamai.collectionDetail.emptyStateSub')}</Sub>
        <Link
          to="/collections"
          style={{
            display: 'inline-flex',
            gap: '0.5rem',
            alignItems: 'center',
            marginTop: '1.25rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontSize: 13,
          }}
        >
          {t('modamai.collectionDetail.browseCollections')} <ArrowRight size={12} />
        </Link>
      </Container>
    );
  }

  const imgSrc = heroImgFromMeta(apiCollection.image);
  const pageContent = String(apiCollection.pageContent || '').trim();

  return (
    <div className="animate-fade-in">
      <PageSeo
        documentTitle={apiCollection.name}
        description={apiCollection.seoDescription || apiCollection.subtitle || ''}
      />
      <ApiLocaleSeo
        canonicalUrl={apiCollection.canonicalUrl}
        hreflangAlternates={apiCollection.hreflangAlternates}
        hreflangLanguages={apiCollection.hreflangLanguages}
      />
      <HeroBand>
        <Container style={{ padding: 'clamp(28px,4vw,48px) 1.5rem', display: 'grid', gap: '1.5rem' }}>
          <BreadcrumbNav aria-label={t('modamai.shop.breadcrumbAria')}>
            <CrumbLink to="/">{t('modamai.shop.home')}</CrumbLink>
            <span aria-hidden>/</span>
            <CrumbLink to="/collections">{t('modamai.collectionDetail.breadcrumbCollections')}</CrumbLink>
            <span aria-hidden>/</span>
            <span style={{ color: 'inherit', opacity: 0.92 }}>{apiCollection.name}</span>
          </BreadcrumbNav>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '1.75rem',
              alignItems: 'center',
              minWidth: 0,
            }}
            className="col-hero-split"
          >
            <style>{`@media(min-width:880px){.col-hero-split{grid-template-columns:1fr 1fr!important;}}`}</style>
            <div
              style={{
                position: 'relative',
                aspectRatio: '4/5',
                overflow: 'hidden',
                background: '#e8e4e0',
                borderRadius: 4,
              }}
            >
              <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ paddingRight: '0.25rem', minWidth: 0 }}>
              <Title>{apiCollection.name}</Title>
              {apiCollection.subtitle ? (
                <p
                  style={{
                    margin: '10px 0 0',
                    fontSize: '12px',
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#524D46',
                  }}
                >
                  {apiCollection.subtitle}
                </p>
              ) : null}
            </div>
          </div>
        </Container>
      </HeroBand>
      <Container style={{ padding: 'clamp(28px,3vw,40px) 1.5rem 4rem' }}>
        <p
          style={{
            margin: '0 0 18px',
            fontSize: '12px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#524D46',
          }}
        >
          {t('modamai.collectionDetail.inventoryCount', { count: items.length })}
        </p>
        {items.length === 0 ? (
          <Sub style={{ marginBottom: '1rem' }}>
            {t('modamai.collectionDetail.noProductsInCollection')}
          </Sub>
        ) : (
          <ProductCardsGrid
            style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(260px,100%),1fr))' }}
          >
            {items.map((p) => (
              <ProductCard key={p.slug || p.id} product={p} />
            ))}
          </ProductCardsGrid>
        )}

        {pageContent ? (
          <PageContentSection>
            <ContentEyebrow>
              {t('modamai.collectionDetail.aboutEyebrow', { defaultValue: 'About this collection' })}
            </ContentEyebrow>
            <CollectionRichHtml html={pageContent} />
          </PageContentSection>
        ) : null}
      </Container>
    </div>
  );
}
